<template>
  <div
    v-if="config"
    class="status-bar"
    :class="`status-bar--${banner}`"
    role="status"
    aria-live="polite">
    <div class="status-bar__inner">
      <b-icon :icon="config.icon" class="status-bar__icon" aria-hidden="true" />
      <span class="status-bar__message">{{ config.message }}</span>
      <b-button
        size="sm"
        class="status-bar__action"
        :disabled="busy"
        @click="act()">
        <i v-if="busy" class="el-icon-loading mr-1"></i>
        {{ busy ? config.busyLabel : config.action }}
      </b-button>
    </div>
  </div>
</template>

<script>
// Persistent connection/session bar, in normal document flow directly below
// WelcomeUser. Replaces the auto-hiding toasts and the old position:fixed
// sessionExpiredBar in Azure.vue.
//
// A toast is the wrong control for these: "session expired" is a STATE, not an
// event. A 3-second toast fires whether or not the user is looking, and if
// they were away they return to a page where every request silently fails.
const STATES = {
  session: {
    icon: 'exclamation-triangle-fill',
    message: 'Your session has expired.',
    action: 'Sign in',
    busyLabel: 'Signing in...'
  },
  network: {
    icon: 'wifi-off',
    message: "Can't reach the server.",
    action: 'Retry',
    busyLabel: 'Retrying...'
  },
  server: {
    icon: 'exclamation-octagon-fill',
    message: 'Something went wrong on the server.',
    action: 'Retry',
    busyLabel: 'Retrying...'
  }
}

export default {
  name: 'StatusBar',
  data() {
    return {
      busy: false
    }
  },
  computed: {
    banner() {
      return this.$store.getters.statusBanner
    },
    config() {
      return STATES[this.banner] ?? null
    }
  },
  watch: {
    banner() {
      // The state changed underneath us (cleared on recovery, or replaced by a
      // different failure) — whatever we were waiting on is no longer current.
      this.busy = false
    }
  },
  methods: {
    act() {
      if (this.busy) {
        return
      }
      this.busy = true
      // Azure.vue owns the axios instance and the sign-in navigation, so it
      // performs the action and calls back when it settles.
      this.$root.$emit('status-bar-action', this.banner, () => {
        this.busy = false
      })
    }
  }
}
</script>

<style scoped>
.status-bar {
  width: 100%;
  font-family: Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.status-bar__inner {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1.5rem;
  /* Wraps instead of overflowing: the old bar used white-space: nowrap and
     pushed itself off screen on narrow viewports. */
  flex-wrap: wrap;
}

.status-bar__icon {
  flex-shrink: 0;
  font-size: 1rem;
}

.status-bar__message {
  flex: 1 1 auto;
  min-width: 0;
}

.status-bar__action {
  flex-shrink: 0;
  font-weight: 600 !important;
  border-radius: 6px !important;
  padding: 0.2rem 0.85rem !important;
}

/* Session expired — amber. Recoverable by the user, not an outage. */
.status-bar--session {
  background-color: #fffbeb;
  color: #92400e;
}
.status-bar--session .status-bar__action {
  background-color: #f59e0b !important;
  border-color: #f59e0b !important;
  color: #ffffff !important;
}
.status-bar--session .status-bar__action:hover:not(:disabled) {
  background-color: #d97706 !important;
  border-color: #d97706 !important;
}

/* Network — slate. Usually the user's own connection. */
.status-bar--network {
  background-color: #f1f5f9;
  color: #334155;
}
.status-bar--network .status-bar__action {
  background-color: #475569 !important;
  border-color: #475569 !important;
  color: #ffffff !important;
}
.status-bar--network .status-bar__action:hover:not(:disabled) {
  background-color: #334155 !important;
  border-color: #334155 !important;
}

/* Server — red. Something is wrong on our side. */
.status-bar--server {
  background-color: #fef2f2;
  color: #991b1b;
}
.status-bar--server .status-bar__action {
  background-color: #dc2626 !important;
  border-color: #dc2626 !important;
  color: #ffffff !important;
}
.status-bar--server .status-bar__action:hover:not(:disabled) {
  background-color: #b91c1c !important;
  border-color: #b91c1c !important;
}

@media (max-width: 575px) {
  .status-bar__inner {
    padding: 0.5rem 1rem;
  }
}
</style>
